import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/services/config-service/config.service';
import {
  CarAggregatedAgeDetails,
  CarAggregatedComplianceDueDetails,
  RailCar,
} from 'src/app/models/railcar.model';
import { OptionModel } from 'src/app/models/railcar-display-options.model';
import {
  complianceDueData,
  railCarAgeData,
  chartOptions,
} from './detail-view-card.component.helper';
import { LoginService } from 'src/app/services/login-service/login.service';

@Component({
  selector: 'app-detail-view-card',
  templateUrl: './detail-view-card.component.html',
  styleUrls: ['./detail-view-card.component.scss'],
})
export class DetailViewCardComponent implements OnInit {
  @Input() carDetails: RailCar = {
    model_id: 0,
    model_desc: '',
    gross_rail_load: '',
    capacity: 0,
    capacity_uom: '',
    gate_type: null,
    gate_size: null,
    hatch_cover: '',
    compartments: null,
    stencil_spec: null,
    insulation: null,
    coils: null,
    pressure: 0,
    country: '',
    state: '',
    count: 0,
    rate: 0,
    rate2: 0,
    file_name: '',
    age: 0,
    compliance_due: '',
  };
  @Input() displayComplianceDue = true;
  @Input() displayedAttr: OptionModel[] = [];
  @Input() blank: boolean = false;
  @Input() selectEnabled: boolean = true;
  @Input() selected: boolean = false;

  @Output() cardToggled: EventEmitter<any> = new EventEmitter();
  @Output('closeModelFn') closeModelFn: EventEmitter<void> = new EventEmitter();

  _imageURL_: any = '';
  imageAltText = '';

  railCarAgeData = railCarAgeData;
  complianceDueData = complianceDueData;
  chartOptions = chartOptions;

  constructor(
    public configService: ConfigService,
    private httpClient: HttpClient,
    private loginService: LoginService
  ) {}

  ngOnInit(): void {
    const { capacity_uom, aggregated, file_name } = this.carDetails;

    let index = this.displayedAttr.findIndex((x) => x.field === 'capacity');
    if (index != -1 && !!capacity_uom) {
      this.displayedAttr[index].labelTail = `(${capacity_uom})`;
    }

    if (!!file_name) this.getImage(file_name);

    if (!aggregated) return;
    this.fillAgeChart(aggregated.aggregated_age_range);
    this.fillComplianceDueChart(aggregated.aggregated_compliance_due);
  }

  getImage(fileName: any): void {
    const token = this.loginService.getSessionToken();
    if (token === null) this.loginService.signout();
    const image$ = this.httpClient.post(
      `${this.configService.imagesURL}/${fileName}`,
      {},
      {
        headers: { Authorization: 'Bearer ' + token },
        observe: 'response',
        responseType: 'blob',
      }
    );
    image$.subscribe({
      next: (img: any) => {
        const reader = new FileReader();
        reader.addEventListener(
          'load',
          () => {
            this._imageURL_ = reader.result;
          },
          false
        );

        if (img) {
          reader.readAsDataURL(img.body);
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  fillAgeChart(ages: CarAggregatedAgeDetails[]): void {
    const rawAges = ages.reduce(
      (res, car) => {
        Object.entries(car).forEach((x) => {
          if (typeof x[1] !== 'number' || !x[0].includes('age')) return;

          if (res[x[0]] === undefined) res[x[0]] = 0;
          res[x[0]] += x[1];
        });
        return res;
      },
      {} as { [x: string]: number }
    );
    let sortedAges = Object.entries(rawAges)
      .map((age) => [+age[0].split('_')[1].split('p')[0], age[1]])
      .sort((a, b) => a[0] - b[0])
      .map((x) => x[1]);
    this.railCarAgeData.datasets[0].data = sortedAges;
  }

  fillComplianceDueChart(compDue: CarAggregatedComplianceDueDetails[]): void {
    const rawCompDue = compDue.reduce(
      (res, car) => {
        if (res[car.compliance_due] === undefined) res[car.compliance_due] = 0;
        res[car.compliance_due] += car.count;
        return res;
      },
      {} as { [x: string]: number }
    );

    for (
      let year = new Date().getFullYear();
      Object.keys(rawCompDue).length < 5;
      year++
    ) {
      if (!!rawCompDue[year]) continue;
      rawCompDue[year] = 0;
    }

    const init: { labels: string[]; values: number[] } = {
      labels: [],
      values: [],
    };
    const { labels, values } = Object.entries(rawCompDue)
      .sort((a, b) => +a[0] - +b[0])
      .reduce(
        (res, year) => ({
          labels: [...res.labels, year[0]],
          values: [...res.values, year[1]],
        }),
        init
      );

    this.complianceDueData.labels = labels;
    this.complianceDueData.datasets[0].data = values;
  }

  checkConditionalDisplayAttr(displayedAttr: OptionModel) {
    if (!displayedAttr.displayWhen) return true;
    const { carAttributeName, value } = displayedAttr.displayWhen;
    return value.includes(this.carDetails[carAttributeName]);
  }

  isNan(value: string | number) {
    return isNaN(value as number);
  }

  closeModel(): void {
    this.closeModelFn.emit();
  }
}
